'use client';

import { useState } from 'react';
import { cn } from '@/app/utils';
import { AngleRight } from '../../icons/AngleRight';
import type { RichTextValue, UseCaseIntroSectionData } from '../../types';
import { MediaAsset } from '../MediaAsset';
import { PortableTextContent } from '../PortableTextContent';
import { SectionShell } from '../section-shared';

function extractPlainText(value?: RichTextValue): string {
  if (!value || !Array.isArray(value)) return '';
  return value
    .filter((block): block is { _type: string; children?: Array<{ text?: string }> } => block._type === 'block')
    .flatMap((block) => block.children?.map((c) => c.text ?? '') ?? [])
    .join(' ')
    .trim();
}

export function TabPreview({ section }: { section: UseCaseIntroSectionData }) {
  const items = section.items ?? [];
  const [activeIndex, setActiveIndex] = useState(0);
  const activeItem = items[activeIndex] ?? items[0];

  if (!activeItem || items.length === 0) return null;

  return (
    <SectionShell>
      <div className="w-full text-center">
        {section.title && (
          <h2 className="font-semibold text-[36px] text-white leading-[1.2] sm:text-[28px]">{section.title}</h2>
        )}
        <PortableTextContent className="mt-3 text-base text-white/50 leading-[1.6]" value={section.description} />
      </div>

      <div className="mt-10 flex items-start gap-5 sm:mt-8 sm:flex-col sm:gap-6">
        {/* 左侧大图预览 */}
        <div className="relative w-[603px] shrink-0 overflow-hidden rounded-[20px] border border-white/10 sm:w-full">
          <MediaAsset className="aspect-[4/3] w-full object-cover" media={activeItem.media} />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/90 via-50% via-black/20 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-8 sm:p-5">
            <h3 className="font-semibold text-[24px] text-white leading-[1.3] sm:text-xl">{activeItem.title}</h3>
            <PortableTextContent
              className="mt-2 max-w-[400px] text-[15px] text-white/60 leading-[1.6]"
              value={activeItem.description}
            />
            {activeItem.button?.href && activeItem.button?.label && (
              <a
                className="mt-4 inline-flex h-[41px] items-center justify-center rounded-full bg-[#0086FF] px-6 font-medium text-sm text-white transition-colors hover:bg-[#0f91ff]"
                href={activeItem.button.href}
                rel={/^https?:\/\//.test(activeItem.button.href) ? 'noreferrer' : undefined}
                target={/^https?:\/\//.test(activeItem.button.href) ? '_blank' : undefined}
              >
                {activeItem.button.label}
              </a>
            )}
          </div>
        </div>

        {/* 右侧列表 */}
        <div className="flex min-w-0 flex-1 flex-col gap-3 sm:w-full">
          {items?.map((item, index) => {
            const isActive = index === activeIndex;
            return (
              <button
                key={item._key || `${item.title}-${index}`}
                className={cn(
                  'flex w-full items-center gap-4 rounded-[14px] border px-[13px] py-[13px] text-left transition-colors',
                  isActive
                    ? 'border-white/15 bg-system-white08'
                    : 'border-transparent bg-system-white02 hover:border-white/10 hover:bg-system-white08',
                )}
                onClick={() => setActiveIndex(index)}
                type="button"
              >
                <div className="h-[60px] w-[80px] shrink-0 overflow-hidden rounded-[10px]">
                  <MediaAsset className="h-full w-full object-cover" media={item.media} />
                </div>
                <div className="min-w-0 flex-1">
                  <p
                    className={cn('font-medium text-[15px] leading-[1.35]', isActive ? 'text-white' : 'text-white/60')}
                  >
                    {item.title}
                  </p>
                  <p className="mt-1 truncate text-[13px] text-white/35 leading-[1.4]">
                    {extractPlainText(item.description)}
                  </p>
                </div>
                <AngleRight className={cn('size-4 shrink-0', isActive ? 'text-white' : 'text-white/35')} />
              </button>
            );
          })}
        </div>
      </div>
    </SectionShell>
  );
}
